/*
 * MELLO — behaviour for the Mello sections (m-*).
 * Plain ES module, no framework. Cart actions go through Horizon's own
 * contract (CartLinesUpdateEvent + /cart/add.js) so its drawer, cart
 * icon and counts update exactly as they do for native product forms.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/*
 * Horizon 4 makes `.page-wrapper` the scroll container on desktop
 * (html/body are 100dvh + overflow:hidden at ≥990px). Always scroll/listen
 * on whichever element is actually scrolling.
 */
const scroller = () =>
  getComputedStyle(document.documentElement).overflowY === 'hidden'
    ? document.querySelector('.page-wrapper') || document.scrollingElement
    : document.scrollingElement;
const onScroll = (fn) => {
  window.addEventListener('scroll', fn, { passive: true });
  document.querySelector('.page-wrapper')?.addEventListener('scroll', fn, { passive: true });
};
const scrollToY = (top) => scroller().scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
/** Distance from the top of the scroll container to an element. */
const offsetInScroller = (el) => {
  const sc = scroller();
  const base = sc === document.scrollingElement ? 0 : sc.getBoundingClientRect().top;
  return el.getBoundingClientRect().top - base + sc.scrollTop;
};
const clamp01 = (v) => Math.min(1, Math.max(0, v));
const range = (v, a, b) => clamp01((v - a) / (b - a));
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const lerp = (a, b, t) => a + (b - a) * t;

/* ── Money ─────────────────────────────────────────────── */
function formatMoney(cents, format = window.MelloConfig?.moneyFormat || '₹{{amount_no_decimals}}') {
  const n = Number(cents) / 100;
  const group = (value, decimals, thousands = ',', dec = '.') => {
    const fixed = value.toFixed(decimals);
    const [int, frac] = fixed.split('.');
    // Indian grouping for INR-style formats, western otherwise
    const indian = /₹|Rs/.test(format);
    const intFmt = indian
      ? int.length > 3
        ? `${int.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, thousands)}${thousands}${int.slice(-3)}`
        : int
      : int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? `${intFmt}${dec}${frac}` : intFmt;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount': return group(n, 2);
      case 'amount_no_decimals': return group(n, 0);
      case 'amount_with_comma_separator': return group(n, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return group(n, 0, '.');
      default: return group(n, 2);
    }
  }).replace(/\.00(?!\d)/, '');
}

/* ── Cart (Horizon contract) ───────────────────────────── */
async function addToCart(sourceEl, lines, productId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const itemCount = lines.reduce((n, l) => n + l.quantity, 0);
  sourceEl.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  let response;
  try {
    response = await fetch(window.MelloConfig.cartAddUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: lines, sections: sectionIds.join(',') }),
    }).then((r) => r.json());
  } catch (e) {
    response = { status: 500, message: 'Network error' };
  }

  const cartItems = document.querySelector('cart-items-component');
  let ajaxCart;
  if (cartItems && typeof cartItems.fetchCartData === 'function') {
    await customElements.whenDefined('cart-items-component');
    ajaxCart = await cartItems.fetchCartData();
  } else {
    ajaxCart = await fetch(`${window.MelloConfig.cartUrl}.js`).then((r) => r.json());
  }

  const didError = Boolean(response?.status);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: {
      items: ajaxCart.items,
      source: 'mello',
      sourceId: 'mello',
      itemCount,
      productId,
      sections: didError ? undefined : response.sections,
      didError,
    },
  });
  if (didError) throw new Error(response.description || response.message || 'Could not add to bag');
  return ajaxCart;
}

function flashAdded(btn) {
  btn.classList.add('is-added');
  setTimeout(() => btn.classList.remove('is-added'), 1600);
}

/* ── Reveal + doodles ──────────────────────────────────── */
function initReveal(root = document) {
  document.documentElement.classList.remove('m-no-js');
  const targets = root.querySelectorAll('[data-reveal], .m-mask, svg[data-draw]');
  if (reduceMotion || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add(el.matches('svg') ? 'is-drawn' : 'is-in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add(e.target.matches('svg') ? 'is-drawn' : 'is-in');
        io.unobserve(e.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px' }
  );
  targets.forEach((el) => {
    if (el.matches('svg[data-draw="now"]')) {
      requestAnimationFrame(() => el.classList.add('is-drawn'));
    } else io.observe(el);
  });
}

/* ── Cocoa dust ────────────────────────────────────────── */
function initDust(canvas) {
  if (reduceMotion) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const color = canvas.dataset.color || '110, 65, 44';
  const low = (navigator.hardwareConcurrency ?? 8) <= 4 || window.innerWidth < 768;
  const n = Math.round((Number(canvas.dataset.count) || 30) * (low ? 0.5 : 1));
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  let w = 0, h = 0, raf = 0, visible = false, last = performance.now();
  const parts = Array.from({ length: n }, () => ({
    x: Math.random(), y: Math.random(), r: 0.6 + Math.random() * 1.9,
    s: 0.00012 + Math.random() * 0.00035, drift: (Math.random() - 0.5) * 0.00018,
    a: 0.18 + Math.random() * 0.45, ph: Math.random() * Math.PI * 2,
  }));
  const resize = () => {
    const r = canvas.getBoundingClientRect();
    w = r.width; h = r.height;
    canvas.width = w * dpr; canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  const tick = (t) => {
    const dt = Math.min(t - last, 50); last = t;
    ctx.clearRect(0, 0, w, h);
    for (const p of parts) {
      p.y -= p.s * dt;
      p.x += p.drift * dt + Math.sin(t * 0.0006 + p.ph) * 0.00008 * dt;
      if (p.y < -0.05) p.y = 1.05;
      if (p.x < -0.05) p.x = 1.05;
      if (p.x > 1.05) p.x = -0.05;
      const edge = Math.min(1, Math.min(p.y, 1 - p.y) * 6);
      ctx.beginPath();
      ctx.fillStyle = `rgba(${color}, ${p.a * edge})`;
      ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    raf = requestAnimationFrame(tick);
  };
  const start = () => { if (!raf && visible && !document.hidden) { last = performance.now(); raf = requestAnimationFrame(tick); } };
  const stop = () => { cancelAnimationFrame(raf); raf = 0; };
  resize();
  new ResizeObserver(resize).observe(canvas);
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; visible ? start() : stop(); }).observe(canvas);
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
}

/* ── Hero ──────────────────────────────────────────────── */
function initRotator(el) {
  const words = [...el.querySelectorAll(':scope > span')];
  if (words.length < 2) return;
  let i = 0;
  const settle = () => words[words.length - 1].querySelector('svg')?.classList.add('is-drawn');
  if (reduceMotion) {
    words.forEach((w) => w.classList.remove('is-current'));
    words[words.length - 1].classList.add('is-current');
    settle();
    return;
  }
  const next = () => {
    if (i >= words.length - 1) return settle();
    words.forEach((w) => w.classList.remove('is-prev'));
    words[i].classList.remove('is-current', 'is-animated');
    words[i].classList.add('is-prev');
    i += 1;
    words[i].classList.add('is-current', 'is-animated');
    setTimeout(next, i === words.length - 1 ? 300 : 1300);
  };
  setTimeout(next, 2400);
}

function initHero(hero) {
  const rotator = hero.querySelector('[data-rotator]');
  if (rotator) initRotator(rotator);

  const stage = hero.querySelector('[data-stage]');
  const pouches = [...hero.querySelectorAll('[data-pouch]')];
  const tint = hero.querySelector('[data-tint]');
  const labels = [...hero.querySelectorAll('[data-label]')];
  const sticky = hero.querySelector('.m-hero__sticky');
  const copy = hero.querySelector('[data-copy]');
  const meet = hero.querySelector('[data-meet]');
  const labelWrap = hero.querySelector('[data-labels]');
  const ambient = [...hero.querySelectorAll('[data-ambient]')];

  // personality tint on hover
  const setHover = (key) => {
    pouches.forEach((p) => p.classList.toggle('is-hover', p.dataset.key === key));
    labels.forEach((l) => l.classList.toggle('is-hover', l.dataset.key === key));
    const pouch = pouches.find((p) => p.dataset.key === key);
    if (tint) {
      tint.style.backgroundColor = pouch ? pouch.dataset.tint : 'transparent';
      tint.style.opacity = pouch ? '0.55' : '0';
    }
  };
  [...pouches, ...labels].forEach((el) => {
    el.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && setHover(el.dataset.key));
    el.addEventListener('pointerleave', () => setHover(null));
  });

  const desktop = window.matchMedia('(min-width: 990px)');
  const pinned = () => desktop.matches && !reduceMotion && hero.hasAttribute('data-pinned');
  const MOVE = [0.08, 0.52];
  const LINEUP_Y = 0.5, LINEUP_H = 0.38;
  let deltas = [];
  let mx = 0, my = 0, tmx = 0, tmy = 0;

  const measure = () => {
    const W = stage.offsetWidth, H = stage.offsetHeight;
    deltas = pouches.map((el) => ({
      x: Number(el.dataset.lineupX) * W - el.offsetLeft,
      y: LINEUP_Y * H - el.offsetTop,
      s: (LINEUP_H * H) / el.offsetHeight,
    }));
  };

  const progress = () => {
    const rect = hero.getBoundingClientRect();
    const total = hero.offsetHeight - sticky.offsetHeight;
    return total > 0 ? clamp01(-rect.top / total) : 0;
  };

  const bgA = [251, 247, 241], bgB = [242, 231, 215];
  let raf = 0;
  const render = () => {
    raf = 0;
    const on = pinned();
    const p = on ? progress() : 0;
    mx += (tmx - mx) * 0.08; my += (tmy - my) * 0.08;
    const t = easeInOut(range(p, MOVE[0], MOVE[1]));
    const fade = 1 - range(p, 0, 0.25);
    pouches.forEach((el, i) => {
      const d = deltas[i] || { x: 0, y: 0, s: 1 };
      const depth = Number(el.dataset.depth) || 1;
      const rot0 = Number(el.dataset.rot) || 0, rot1 = Number(el.dataset.lineupRot) || 0;
      const arc = Math.sin(t * Math.PI) * -28 * (el.dataset.pos === 'front' ? 1.6 : 1);
      const px = on ? mx * depth * 16 * fade : 0, py = on ? my * depth * 10 * fade : 0;
      const x = d.x * t + px, y = d.y * t + arc * (on ? 1 : 0) + py;
      const s = 1 + (d.s - 1) * t;
      el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s}) rotate(${lerp(rot0, rot1, t)}deg)`;
    });
    if (on) {
      const c = bgA.map((v, i) => Math.round(lerp(v, bgB[i], range(p, 0.1, 0.5))));
      sticky.style.backgroundColor = `rgb(${c.join(',')})`;
      const co = 1 - range(p, 0.03, 0.24);
      copy.style.opacity = String(co);
      copy.style.transform = `translate3d(0, ${-90 * range(p, 0, 0.3)}px, 0)`;
      copy.style.pointerEvents = co < 0.15 ? 'none' : '';
      ambient.forEach((a) => (a.style.opacity = String(1 - range(p, 0.04, 0.22))));
      if (meet) {
        meet.style.opacity = String(range(p, 0.42, 0.58));
        meet.style.transform = `translate3d(0, ${40 * (1 - range(p, 0.42, 0.6))}px, 0)`;
      }
      if (labelWrap) {
        const lo = range(p, 0.5, 0.64);
        labelWrap.style.opacity = String(lo);
        labelWrap.style.transform = `translate3d(0, ${24 * (1 - range(p, 0.5, 0.66))}px, 0)`;
        labelWrap.style.pointerEvents = lo > 0.5 ? 'auto' : 'none';
      }
    } else {
      sticky.style.backgroundColor = '';
      copy.style.opacity = '';
      copy.style.transform = '';
      copy.style.pointerEvents = '';
    }
    if (on && (Math.abs(tmx - mx) > 0.001 || Math.abs(tmy - my) > 0.001)) queue();
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(render); };

  measure();
  new ResizeObserver(() => { measure(); queue(); }).observe(stage);
  onScroll(queue);
  desktop.addEventListener('change', () => { measure(); queue(); });
  window.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' || !pinned()) return;
    tmx = (e.clientX / window.innerWidth) * 2 - 1;
    tmy = (e.clientY / window.innerHeight) * 2 - 1;
    queue();
  }, { passive: true });
  queue();

  // "Explore the collection" → glide to the lineup (desktop) or the carousel (mobile)
  hero.querySelector('[data-explore]')?.addEventListener('click', (e) => {
    e.preventDefault();
    if (pinned()) scrollToY(offsetInScroller(hero) + (hero.offsetHeight - sticky.offsetHeight) * 0.7);
    else {
      const meet = document.querySelector('[data-meet-mobile]');
      if (meet) scrollToY(offsetInScroller(meet) - 60);
    }
  });
}

/* ── Product cards: variant pills + add to bag ─────────── */
function initCard(card) {
  const pills = [...card.querySelectorAll('[data-variant]')];
  const btn = card.querySelector('[data-add]');
  const price = card.querySelector('[data-price]');
  const compare = card.querySelector('[data-compare]');
  const select = (pill) => {
    pills.forEach((p) => p.setAttribute('aria-pressed', String(p === pill)));
    if (!btn) return;
    btn.dataset.variantId = pill.dataset.variant;
    const available = pill.dataset.available === 'true';
    btn.disabled = !available;
    btn.querySelector('[data-label]').textContent = available ? btn.dataset.addText : btn.dataset.soldText;
    if (price) price.textContent = pill.dataset.price;
    if (compare) {
      compare.textContent = pill.dataset.compare || '';
      compare.hidden = !pill.dataset.compare;
    }
  };
  pills.forEach((p) => p.addEventListener('click', () => select(p)));
  btn?.addEventListener('click', async () => {
    if (btn.disabled) return;
    btn.setAttribute('aria-busy', 'true');
    try {
      await addToCart(btn, [{ id: Number(btn.dataset.variantId), quantity: 1 }], card.dataset.productId);
      flashAdded(btn);
    } catch (e) {
      console.warn('[mello] add to bag failed', e);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });
}

/* ── Build your Mello box ──────────────────────────────── */
function initBox(box) {
  const SLOTS = 4, MIN = 2;
  const rows = [...box.querySelectorAll('[data-box-item]')];
  const slots = [...box.querySelectorAll('[data-slot]')];
  const counter = box.querySelector('[data-box-count]');
  const total = box.querySelector('[data-box-total]');
  const addBtn = box.querySelector('[data-box-add]');
  const hint = box.querySelector('[data-box-hint]');
  const gift = box.querySelector('[data-box-gift]');
  const noteField = box.querySelector('[data-box-note-input]');
  const noteCard = box.querySelector('[data-box-note]');
  const noteText = box.querySelector('[data-box-note-text]');
  let items = [];

  const byId = Object.fromEntries(rows.map((r) => [r.dataset.variantId, r]));

  const render = () => {
    slots.forEach((slot, i) => {
      const id = items[i];
      const row = id ? byId[id] : null;
      const cur = slot.querySelector('.m-slot__item');
      if (row && cur?.dataset.id === id) return;
      slot.innerHTML = '';
      if (row) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'm-slot__item';
        b.dataset.id = id;
        b.style.setProperty('--tint', row.dataset.tint);
        b.setAttribute('aria-label', `Remove ${row.dataset.name} from box`);
        const img = document.createElement('img');
        img.src = row.dataset.img;
        img.alt = '';
        b.append(img);
        b.addEventListener('click', () => { items.splice(i, 1); render(); });
        slot.append(b);
      } else {
        slot.innerHTML = '<span class="m-slot__empty" aria-hidden="true">+</span>';
      }
    });
    rows.forEach((r) => {
      const c = items.filter((x) => x === r.dataset.variantId).length;
      r.querySelector('output').textContent = String(c);
      r.querySelector('[data-minus]').disabled = c === 0;
      r.querySelector('[data-plus]').disabled = items.length >= SLOTS;
    });
    counter.textContent = `${items.length}/${SLOTS}`;
    total.textContent = formatMoney(items.reduce((t, id) => t + Number(byId[id].dataset.price), 0));
    addBtn.disabled = items.length < MIN;
    addBtn.querySelector('[data-label]').textContent =
      items.length < MIN ? addBtn.dataset.needText.replace('[n]', MIN - items.length) : addBtn.dataset.addText;
    if (hint) hint.hidden = items.length > 0;
  };

  rows.forEach((r) => {
    r.querySelector('[data-plus]').addEventListener('click', () => { if (items.length < SLOTS) { items.push(r.dataset.variantId); render(); } });
    r.querySelector('[data-minus]').addEventListener('click', () => {
      const i = items.lastIndexOf(r.dataset.variantId);
      if (i >= 0) { items.splice(i, 1); render(); }
    });
  });

  gift?.addEventListener('change', () => {
    noteField.hidden = !gift.checked;
    noteCard.hidden = !gift.checked;
    if (gift.checked) noteField.focus();
  });
  noteField?.addEventListener('input', () => { noteText.textContent = noteField.value || noteText.dataset.placeholder; });

  addBtn.addEventListener('click', async () => {
    if (items.length < MIN) return;
    const note = gift?.checked ? noteField.value.trim() : '';
    const boxId = `MELLO-BOX-${Date.now().toString(36).toUpperCase()}`;
    const counts = {};
    items.forEach((id) => (counts[id] = (counts[id] || 0) + 1));
    const lines = Object.entries(counts).map(([id, quantity], i) => ({
      id: Number(id),
      quantity,
      properties: { _box: boxId, ...(i === 0 && note ? { 'Gift note': note } : {}) },
    }));
    addBtn.setAttribute('aria-busy', 'true');
    try {
      await addToCart(addBtn, lines);
      flashAdded(addBtn);
      items = [];
      if (gift) { gift.checked = false; noteField.value = ''; noteField.hidden = true; noteCard.hidden = true; }
      render();
    } catch (e) {
      console.warn('[mello] box add failed', e);
    } finally {
      addBtn.removeAttribute('aria-busy');
    }
  });

  render();
}

/* ── Contact ───────────────────────────────────────────── */
function initContact(sec) {
  const topicInput = sec.querySelector('[data-topic-input]');
  sec.querySelectorAll('[data-topic]').forEach((b) =>
    b.addEventListener('click', () => {
      const on = b.getAttribute('aria-pressed') !== 'true';
      sec.querySelectorAll('[data-topic]').forEach((x) => x.setAttribute('aria-pressed', 'false'));
      b.setAttribute('aria-pressed', String(on));
      if (topicInput) topicInput.value = on ? b.dataset.topic : '';
    })
  );

  const copy = sec.querySelector('[data-copy-email]');
  copy?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(copy.dataset.copyEmail);
      copy.textContent = copy.dataset.done;
      setTimeout(() => (copy.textContent = copy.dataset.label), 1800);
    } catch {
      window.location.href = `mailto:${copy.dataset.copyEmail}`;
    }
  });

  // Friendlier validation than the browser's defaults
  const form = sec.querySelector('form');
  if (!form) return;
  const msgs = {
    name: 'We’ll need your name.',
    email: 'That email looks a little too relaxed.',
    body: 'Tell us a little more?',
  };
  form.setAttribute('novalidate', '');
  form.addEventListener('submit', (e) => {
    let firstBad = null;
    form.querySelectorAll('[data-field]').forEach((f) => {
      const input = f.querySelector('input, textarea');
      const key = f.dataset.field;
      let bad = false;
      if (key === 'email') bad = !/^\S+@\S+\.\S+$/.test(input.value.trim());
      else if (key === 'name' || key === 'body') bad = input.value.trim().length < (key === 'body' ? 2 : 1);
      f.classList.toggle('has-error', bad);
      let err = f.querySelector('.m-field__err');
      if (bad) {
        if (!err) { err = document.createElement('p'); err.className = 'm-field__err'; err.setAttribute('role', 'alert'); f.append(err); }
        err.textContent = msgs[key];
        firstBad ||= input;
      } else err?.remove();
    });
    if (firstBad) { e.preventDefault(); firstBad.focus(); }
  });
  form.querySelectorAll('input, textarea').forEach((i) =>
    i.addEventListener('input', () => { const f = i.closest('[data-field]'); f?.classList.remove('has-error'); f?.querySelector('.m-field__err')?.remove(); })
  );
}

/* "Contact" links glide to the contact chapter when it's on this page */
function initContactLinks() {
  document.addEventListener('click', (e) => {
    const a = e.target instanceof Element ? e.target.closest('a[href]') : null;
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || !/\/pages\/contact\/?$/.test(url.pathname)) return;
    const target = document.getElementById('contact');
    if (!target || location.pathname.startsWith('/pages/contact')) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', '#contact');
  });
}

/* ── Boot ──────────────────────────────────────────────── */
function boot(root = document) {
  initReveal(root);
  root.querySelectorAll('[data-dust]').forEach(initDust);
  root.querySelectorAll('[data-mello-hero]').forEach(initHero);
  root.querySelectorAll('[data-mello-card]').forEach(initCard);
  root.querySelectorAll('[data-mello-box]').forEach(initBox);
  root.querySelectorAll('[data-mello-contact]').forEach(initContact);
}
boot();
initContactLinks();

// Theme editor: re-initialise a section when it's re-rendered
document.addEventListener('shopify:section:load', (e) => boot(e.target));
